import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { TaxBracketService } from '../../services/payrollService';
import { TaxBracket } from '../../types/payroll';
import { formatCurrency, formatPercent, formatDate } from '../../utils/formatters';
import { useRole } from '../../hooks/useRole';
import ConfirmDialog from '../common/ConfirmDialog';

const TaxBracketList: React.FC = () => {
  const [taxBrackets, setTaxBrackets] = useState<TaxBracket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedBracket, setSelectedBracket] = useState<TaxBracket | null>(null);
  const [formData, setFormData] = useState<Partial<TaxBracket>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  const { hasAnyRole } = useRole();
  const isAdmin = hasAnyRole(['admin']);

  useEffect(() => {
    fetchTaxBrackets();
  }, []);

  const fetchTaxBrackets = async () => {
    try {
      setLoading(true);
      const response = await TaxBracketService.getAllTaxBrackets();
      setTaxBrackets(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tax brackets');
      console.error('Error fetching tax brackets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedBracket(null);
    setFormData({
      income_from: 0,
      income_to: 0,
      tax_rate: 0,
      offset_amount: 0,
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: null
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleEdit = (bracket: TaxBracket) => {
    setSelectedBracket(bracket);
    setFormData({
      income_from: bracket.income_from,
      income_to: bracket.income_to,
      tax_rate: bracket.tax_rate,
      offset_amount: bracket.offset_amount,
      effective_from: bracket.effective_from,
      effective_to: bracket.effective_to || null
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleDelete = (bracket: TaxBracket) => {
    setSelectedBracket(bracket);
    setDeleteDialogOpen(true);
  };

  const handleFormChange = (field: keyof TaxBracket, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    // Clear error for the changed field
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (formData.income_from === undefined || formData.income_from === null) {
      errors.income_from = 'Income from is required';
    }
    
    if (formData.income_to === undefined || formData.income_to === null) {
      errors.income_to = 'Income to is required';
    }
    
    if (formData.income_from !== undefined && 
        formData.income_to !== undefined && 
        formData.income_from >= formData.income_to) {
      errors.income_to = 'Must be greater than Income from';
    }
    
    if (formData.tax_rate === undefined || formData.tax_rate === null) {
      errors.tax_rate = 'Tax rate is required';
    } else if (formData.tax_rate < 0 || formData.tax_rate > 100) {
      errors.tax_rate = 'Tax rate must be between 0 and 100';
    }
    
    if (formData.effective_from === undefined || formData.effective_from === null) {
      errors.effective_from = 'Effective from date is required';
    }
    
    if (formData.effective_from && formData.effective_to && 
        new Date(formData.effective_from) >= new Date(formData.effective_to)) {
      errors.effective_to = 'Must be after Effective from date';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      if (selectedBracket) {
        // Update existing bracket
        await TaxBracketService.updateTaxBracket(selectedBracket.id, formData);
      } else {
        // Create new bracket
        await TaxBracketService.createTaxBracket(formData);
      }
      
      // Refresh the list
      fetchTaxBrackets();
      setEditDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save tax bracket');
      console.error('Error saving tax bracket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedBracket) return;
    
    try {
      setLoading(true);
      await TaxBracketService.deleteTaxBracket(selectedBracket.id);
      
      // Refresh the list
      fetchTaxBrackets();
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete tax bracket');
      console.error('Error deleting tax bracket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Tax Brackets Management
          </Typography>
          {isAdmin && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleAddNew}
            >
              Add Tax Bracket
            </Button>
          )}
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 3 }} />}
        
        {error && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Income From</TableCell>
                <TableCell>Income To</TableCell>
                <TableCell>Tax Rate</TableCell>
                <TableCell>Offset Amount</TableCell>
                <TableCell>Effective From</TableCell>
                <TableCell>Effective To</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {taxBrackets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                    {loading ? "Loading..." : "No tax brackets found"}
                  </TableCell>
                </TableRow>
              ) : (
                taxBrackets.map((bracket) => {
                  const isActive = !bracket.effective_to || new Date(bracket.effective_to) >= new Date();
                  
                  return (
                    <TableRow key={bracket.id} hover>
                      <TableCell>{formatCurrency(bracket.income_from, 'VND')}</TableCell>
                      <TableCell>{formatCurrency(bracket.income_to, 'VND')}</TableCell>
                      <TableCell>{formatPercent(bracket.tax_rate)}</TableCell>
                      <TableCell>{formatCurrency(bracket.offset_amount, 'VND')}</TableCell>
                      <TableCell>{formatDate(bracket.effective_from)}</TableCell>
                      <TableCell>{bracket.effective_to ? formatDate(bracket.effective_to) : '—'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={isActive ? 'Active' : 'Expired'} 
                          color={isActive ? 'success' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="center">
                          <Box>
                            <Tooltip title="Edit Bracket">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEdit(bracket)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete Bracket">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(bracket)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Edit/Add Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedBracket ? 'Edit Tax Bracket' : 'Add New Tax Bracket'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Income From (VND)"
              type="number"
              value={formData.income_from ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('income_from', Number(e.target.value))}
              fullWidth
              error={!!formErrors.income_from}
              helperText={formErrors.income_from}
              InputProps={{
                inputProps: { min: 0, step: 1000000 }
              }}
            />
            
            <TextField
              label="Income To (VND)"
              type="number"
              value={formData.income_to ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('income_to', Number(e.target.value))}
              fullWidth
              error={!!formErrors.income_to}
              helperText={formErrors.income_to}
              InputProps={{
                inputProps: { min: 0, step: 1000000 }
              }}
            />
            
            <TextField
              label="Tax Rate (%)"
              type="number"
              value={formData.tax_rate ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('tax_rate', Number(e.target.value))}
              fullWidth
              error={!!formErrors.tax_rate}
              helperText={formErrors.tax_rate}
              InputProps={{
                inputProps: { min: 0, max: 100, step: 0.1 }
              }}
            />
            
            <TextField
              label="Offset Amount (VND)"
              type="number"
              value={formData.offset_amount ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFormChange('offset_amount', Number(e.target.value))}
              fullWidth
              InputProps={{
                inputProps: { min: 0, step: 10000 }
              }}
            />
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Effective From"
                  value={formData.effective_from ? new Date(formData.effective_from) : null}
                  onChange={(date: Date | null) => handleFormChange('effective_from', date?.toISOString().split('T')[0] || null)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.effective_from,
                      helperText: formErrors.effective_from
                    }
                  }}
                />
                
                <DatePicker
                  label="Effective To (Optional)"
                  value={formData.effective_to ? new Date(formData.effective_to) : null}
                  onChange={(date: Date | null) => handleFormChange('effective_to', date?.toISOString().split('T')[0] || null)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.effective_to,
                      helperText: formErrors.effective_to
                    }
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Tax Bracket"
        content={`Are you sure you want to delete the tax bracket with rate ${selectedBracket?.tax_rate}% for income from ${formatCurrency(selectedBracket?.income_from || 0, 'VND')}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmColor="error"
        confirmText="Delete"
      />
    </Container>
  );
};

export default TaxBracketList;
